// Composant Chatbot Flottant avec FAQ
import React, { useState, useRef, useEffect } from 'react';
import styled from 'styled-components';
import { theme } from '@theme';
import {
  FiMessageCircle, FiX, FiSend, FiChevronDown, FiChevronUp,
  FiSearch, FiHelpCircle
} from 'react-icons/fi';

// Types
interface FAQItem {
  id: string;
  question: string;
  answer: string;
  category: string;
}

interface ChatMessage {
  id: string;
  text: string;
  sender: 'user' | 'bot';
  timestamp: Date;
}

// Styled Components
const FloatingButton = styled.button<{ isOpen: boolean }>`
  position: fixed;
  bottom: ${theme.spacing['2xl']};
  right: ${theme.spacing['2xl']};
  width: 60px;
  height: 60px;
  border-radius: ${theme.borderRadius.full};
  background: ${props => props.isOpen ? theme.colors.error : theme.colors.primary};
  color: white;
  border: none;
  box-shadow: 0 4px 20px rgba(37, 99, 235, 0.4);
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 24px;
  transition: all ${theme.transitions.base};
  z-index: 1000;

  &:hover {
    transform: scale(1.1);
    box-shadow: 0 6px 30px rgba(37, 99, 235, 0.5);
  }

  @media (max-width: 768px) {
    bottom: ${theme.spacing.lg};
    right: ${theme.spacing.lg};
    width: 56px;
    height: 56px;
  }
`;

const ChatWindow = styled.div<{ isOpen: boolean }>`
  position: fixed;
  bottom: ${theme.spacing['2xl']};
  right: ${theme.spacing['2xl']};
  width: 420px;
  height: 600px;
  background: white;
  border-radius: ${theme.borderRadius.lg};
  box-shadow: 0 8px 40px rgba(0, 0, 0, 0.15);
  display: flex;
  flex-direction: column;
  opacity: ${props => props.isOpen ? '1' : '0'};
  transform: ${props => props.isOpen ? 'translateY(0) scale(1)' : 'translateY(20px) scale(0.95)'};
  pointer-events: ${props => props.isOpen ? 'all' : 'none'};
  transition: all ${theme.transitions.base};
  z-index: 999;
  overflow: hidden;

  @media (max-width: 768px) {
    width: calc(100vw - ${theme.spacing.lg} * 2);
    height: calc(100vh - 120px);
    bottom: ${theme.spacing.lg};
    right: ${theme.spacing.lg};
  }
`;

const ChatHeader = styled.div`
  padding: ${theme.spacing.lg};
  background: ${theme.colors.primary};
  color: white;
  display: flex;
  justify-content: space-between;
  align-items: center;
  border-radius: ${theme.borderRadius.lg} ${theme.borderRadius.lg} 0 0;
`;

const HeaderTitle = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing.xs};
`;

const Title = styled.h3`
  font-size: ${theme.typography.sizes.lg};
  font-weight: ${theme.typography.weights.bold};
  margin: 0;
`;

const Subtitle = styled.p`
  font-size: ${theme.typography.sizes.sm};
  margin: 0;
  opacity: 0.9;
`;

const CloseButton = styled.button`
  background: transparent;
  border: none;
  color: white;
  font-size: 24px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: ${theme.spacing.xs};
  border-radius: ${theme.borderRadius.md};
  transition: all ${theme.transitions.fast};

  &:hover {
    background: rgba(255, 255, 255, 0.2);
  }
`;

const TabBar = styled.div`
  display: flex;
  background: ${theme.colors.surface}50;
  border-bottom: 2px solid ${theme.colors.surface};
`;

const Tab = styled.button<{ active: boolean }>`
  flex: 1;
  padding: ${theme.spacing.md};
  background: ${props => props.active ? 'white' : 'transparent'};
  border: none;
  border-bottom: 3px solid ${props => props.active ? theme.colors.primary : 'transparent'};
  color: ${props => props.active ? theme.colors.primary : theme.colors.text + '80'};
  font-size: ${theme.typography.sizes.sm};
  font-weight: ${theme.typography.weights.semibold};
  cursor: pointer;
  transition: all ${theme.transitions.fast};
  display: flex;
  align-items: center;
  justify-content: center;
  gap: ${theme.spacing.xs};

  &:hover {
    background: ${props => props.active ? 'white' : theme.colors.surface}30;
  }
`;

const ChatContent = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow: hidden;
`;

// Chat Tab Styles
const MessagesArea = styled.div`
  flex: 1;
  padding: ${theme.spacing.lg};
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing.md};
  background: ${theme.colors.background};

  &::-webkit-scrollbar {
    width: 6px;
  }

  &::-webkit-scrollbar-track {
    background: ${theme.colors.surface};
  }

  &::-webkit-scrollbar-thumb {
    background: ${theme.colors.primary}40;
    border-radius: ${theme.borderRadius.full};
  }
`;

const MessageBubble = styled.div<{ sender: 'user' | 'bot' }>`
  max-width: 80%;
  padding: ${theme.spacing.md} ${theme.spacing.lg};
  border-radius: ${theme.borderRadius.lg};
  background: ${props => props.sender === 'user' ? theme.colors.primary : 'white'};
  color: ${props => props.sender === 'user' ? 'white' : theme.colors.text};
  align-self: ${props => props.sender === 'user' ? 'flex-end' : 'flex-start'};
  box-shadow: ${theme.shadows.sm};
  line-height: 1.5;
  font-size: ${theme.typography.sizes.sm};
`;

const MessageTime = styled.div<{ sender: 'user' | 'bot' }>`
  font-size: ${theme.typography.sizes.xs};
  color: ${theme.colors.text}60;
  align-self: ${props => props.sender === 'user' ? 'flex-end' : 'flex-start'};
  margin-top: -${theme.spacing.xs};
`;

const InputArea = styled.div`
  padding: ${theme.spacing.lg};
  border-top: 2px solid ${theme.colors.surface};
  background: white;
  display: flex;
  gap: ${theme.spacing.sm};
`;

const Input = styled.input`
  flex: 1;
  padding: ${theme.spacing.sm} ${theme.spacing.md};
  border: 2px solid ${theme.colors.surface};
  border-radius: ${theme.borderRadius.md};
  font-size: ${theme.typography.sizes.sm};
  transition: all ${theme.transitions.fast};

  &:focus {
    outline: none;
    border-color: ${theme.colors.primary};
    box-shadow: 0 0 0 3px ${theme.colors.primary}15;
  }
`;

const SendButton = styled.button`
  padding: ${theme.spacing.sm} ${theme.spacing.md};
  background: ${theme.colors.primary};
  color: white;
  border: none;
  border-radius: ${theme.borderRadius.md};
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: ${theme.spacing.xs};
  font-weight: ${theme.typography.weights.semibold};
  font-size: ${theme.typography.sizes.sm};
  transition: all ${theme.transitions.fast};

  &:hover {
    background: ${theme.colors.primary}DD;
    transform: translateY(-2px);
  }

  &:disabled {
    background: ${theme.colors.text}40;
    cursor: not-allowed;
    transform: none;
  }
`;

// FAQ Tab Styles
const FAQContent = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: ${theme.spacing.lg};

  &::-webkit-scrollbar {
    width: 6px;
  }

  &::-webkit-scrollbar-track {
    background: ${theme.colors.surface};
  }

  &::-webkit-scrollbar-thumb {
    background: ${theme.colors.primary}40;
    border-radius: ${theme.borderRadius.full};
  }
`;

const SearchBar = styled.div`
  position: relative;
  margin-bottom: ${theme.spacing.lg};
`;

const SearchInput = styled.input`
  width: 100%;
  padding: ${theme.spacing.sm} ${theme.spacing.md};
  padding-left: 36px;
  border: 2px solid ${theme.colors.surface};
  border-radius: ${theme.borderRadius.md};
  font-size: ${theme.typography.sizes.sm};
  transition: all ${theme.transitions.fast};

  &:focus {
    outline: none;
    border-color: ${theme.colors.primary};
    box-shadow: 0 0 0 3px ${theme.colors.primary}15;
  }
`;

const SearchIcon = styled.div`
  position: absolute;
  left: ${theme.spacing.sm};
  top: 50%;
  transform: translateY(-50%);
  color: ${theme.colors.text}60;
  font-size: 16px;
`;

const CategoryFilter = styled.div`
  display: flex;
  gap: ${theme.spacing.xs};
  margin-bottom: ${theme.spacing.lg};
  flex-wrap: wrap;
`;

const CategoryChip = styled.button<{ active: boolean }>`
  padding: ${theme.spacing.xs} ${theme.spacing.sm};
  background: ${props => props.active ? theme.colors.primary : theme.colors.surface};
  color: ${props => props.active ? 'white' : theme.colors.text};
  border: none;
  border-radius: ${theme.borderRadius.full};
  font-size: ${theme.typography.sizes.xs};
  font-weight: ${theme.typography.weights.medium};
  cursor: pointer;
  transition: all ${theme.transitions.fast};

  &:hover {
    background: ${props => props.active ? theme.colors.primary + 'DD' : theme.colors.primary}20;
  }
`;

const FAQList = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing.sm};
`;

const FAQItemCard = styled.div`
  border: 1px solid ${theme.colors.surface};
  border-radius: ${theme.borderRadius.md};
  overflow: hidden;
  background: white;
  transition: all ${theme.transitions.fast};

  &:hover {
    box-shadow: ${theme.shadows.sm};
  }
`;

const FAQQuestion = styled.button`
  width: 100%;
  padding: ${theme.spacing.md};
  background: white;
  border: none;
  text-align: left;
  display: flex;
  justify-content: space-between;
  align-items: center;
  cursor: pointer;
  font-size: ${theme.typography.sizes.sm};
  font-weight: ${theme.typography.weights.semibold};
  color: ${theme.colors.text};
  transition: all ${theme.transitions.fast};

  &:hover {
    background: ${theme.colors.surface};
  }
`;

const FAQAnswer = styled.div<{ isOpen: boolean }>`
  max-height: ${props => props.isOpen ? '300px' : '0'};
  overflow: hidden;
  transition: max-height 0.3s ease;
  padding: ${props => props.isOpen ? theme.spacing.md : '0 ' + theme.spacing.md};
  background: ${theme.colors.surface}50;
  color: ${theme.colors.text}90;
  line-height: 1.5;
  font-size: ${theme.typography.sizes.sm};
`;

const EmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: ${theme.spacing['2xl']};
  color: ${theme.colors.text}60;
  text-align: center;
`;

// FAQ Data
const faqData: FAQItem[] = [
  {
    id: '1',
    question: 'Comment améliorer mon score ?',
    answer: 'Assurez-vous que tous vos documents sont à jour et vérifiés. Téléchargez tous les justificatifs demandés. Plus votre dossier est complet, plus votre score sera élevé.',
    category: 'Score'
  },
  {
    id: '2',
    question: 'Quels documents fournir ?',
    answer: 'Les documents requis dépendent de votre profil : pièce d\'identité, justificatif de domicile, avis d\'imposition, justificatifs de revenus (fiches de paie, KBIS, déclaration sur l\'honneur selon votre situation).',
    category: 'Documents'
  },
  {
    id: '3',
    question: 'Délai de vérification ?',
    answer: 'La vérification prend entre 24 et 72 heures ouvrées. Vous recevrez une notification dès que vos documents seront vérifiés.',
    category: 'Documents'
  },
  {
    id: '4',
    question: 'Suivre ma candidature ?',
    answer: 'Consultez "Mes Candidatures" pour voir le statut : En attente, En cours de vérification, Présélectionné, En commission, Accepté ou Rejeté.',
    category: 'Candidature'
  },
  {
    id: '5',
    question: 'Postuler à plusieurs logements ?',
    answer: 'Oui, vous pouvez postuler à plusieurs logements simultanément. Chaque candidature est traitée indépendamment.',
    category: 'Candidature'
  },
  {
    id: '6',
    question: 'Après la présélection ?',
    answer: 'Votre dossier est transmis à la commission d\'attribution qui prend la décision finale. Vous serez notifié sous 15 jours ouvrés.',
    category: 'Processus'
  },
  {
    id: '7',
    question: 'Contacter un gestionnaire ?',
    answer: 'Envoyez un message via la section "Messages". Sélectionnez la candidature concernée. Réponse sous 48 heures ouvrées.',
    category: 'Support'
  },
  {
    id: '8',
    question: 'Sécurité des données ?',
    answer: 'Vos données sont protégées selon les normes RGPD. Vos documents sont chiffrés et seuls les gestionnaires autorisés peuvent y accéder.',
    category: 'Sécurité'
  },
  {
    id: '9',
    question: 'Modifier mon profil ?',
    answer: 'Vous pouvez modifier vos informations à tout moment dans "Profil". Certaines modifications peuvent nécessiter la resoumission de documents.',
    category: 'Profil'
  },
  {
    id: '10',
    question: 'Document rejeté ?',
    answer: 'Vous recevrez une notification avec la raison du rejet. Consultez les instructions, corrigez le problème et retéléchargez le document.',
    category: 'Documents'
  }
];

export const FloatingChatbot: React.FC = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [activeTab, setActiveTab] = useState<'chat' | 'faq'>('chat');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<string>('Tous');
  const [openFAQId, setOpenFAQId] = useState<string | null>(null);
  const [chatMessages, setChatMessages] = useState<ChatMessage[]>([
    {
      id: '1',
      text: 'Bonjour ! Je suis l\'assistant P-Screen. Comment puis-je vous aider ?',
      sender: 'bot',
      timestamp: new Date()
    }
  ]);
  const [inputMessage, setInputMessage] = useState('');
  const messagesEndRef = useRef<HTMLDivElement>(null);

  // Scroll to bottom when new messages arrive
  useEffect(() => {
    if (activeTab === 'chat' && messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [chatMessages, activeTab]);

  // Get unique categories
  const categories = ['Tous', ...Array.from(new Set(faqData.map(item => item.category)))];

  // Filter FAQ items
  const filteredFAQ = faqData.filter(item => {
    const matchesSearch = item.question.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         item.answer.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesCategory = selectedCategory === 'Tous' || item.category === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  const handleSendMessage = () => {
    if (!inputMessage.trim()) return;

    const userMessage: ChatMessage = {
      id: Date.now().toString(),
      text: inputMessage,
      sender: 'user',
      timestamp: new Date()
    };

    setChatMessages(prev => [...prev, userMessage]);

    // Simulate bot response
    setTimeout(() => {
      const botResponse: ChatMessage = {
        id: (Date.now() + 1).toString(),
        text: getBotResponse(inputMessage),
        sender: 'bot',
        timestamp: new Date()
      };
      setChatMessages(prev => [...prev, botResponse]);
    }, 1000);

    setInputMessage('');
  };

  const getBotResponse = (message: string): string => {
    const lowerMessage = message.toLowerCase();

    if (lowerMessage.includes('score')) {
      return 'Pour améliorer votre score, assurez-vous que tous vos documents sont à jour et vérifiés. Consultez la section "Mon Score" pour voir les actions recommandées.';
    }
    if (lowerMessage.includes('document')) {
      return 'Les documents requis dépendent de votre profil. Consultez la section "Documents" pour voir la liste complète.';
    }
    if (lowerMessage.includes('candidature')) {
      return 'Vous pouvez suivre toutes vos candidatures dans "Mes Candidatures". Chaque candidature affiche son statut actuel.';
    }
    if (lowerMessage.includes('délai') || lowerMessage.includes('temps')) {
      return 'La vérification prend 24-72h ouvrées. La décision de la commission intervient sous 15 jours après la présélection.';
    }
    if (lowerMessage.includes('aide') || lowerMessage.includes('help')) {
      return 'Je peux vous aider avec vos candidatures, documents, score et questions générales. Consultez aussi notre FAQ pour plus d\'informations !';
    }

    return 'Je comprends votre question. Consultez notre FAQ ou contactez un gestionnaire via Messages pour une assistance personnalisée.';
  };

  return (
    <>
      <FloatingButton isOpen={isOpen} onClick={() => setIsOpen(!isOpen)}>
        {isOpen ? <FiX /> : <FiMessageCircle />}
      </FloatingButton>

      <ChatWindow isOpen={isOpen}>
        <ChatHeader>
          <HeaderTitle>
            <Title>Assistant P-Screen</Title>
            <Subtitle>Posez vos questions ou consultez la FAQ</Subtitle>
          </HeaderTitle>
          <CloseButton onClick={() => setIsOpen(false)}>
            <FiX />
          </CloseButton>
        </ChatHeader>

        <TabBar>
          <Tab active={activeTab === 'chat'} onClick={() => setActiveTab('chat')}>
            <FiMessageCircle size={16} /> Chat
          </Tab>
          <Tab active={activeTab === 'faq'} onClick={() => setActiveTab('faq')}>
            <FiHelpCircle size={16} /> FAQ
          </Tab>
        </TabBar>

        <ChatContent>
          {activeTab === 'chat' ? (
            <>
              <MessagesArea>
                {chatMessages.map(message => (
                  <React.Fragment key={message.id}>
                    <MessageBubble sender={message.sender}>
                      {message.text}
                    </MessageBubble>
                    <MessageTime sender={message.sender}>
                      {message.timestamp.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}
                    </MessageTime>
                  </React.Fragment>
                ))}
                <div ref={messagesEndRef} />
              </MessagesArea>

              <InputArea>
                <Input
                  type="text"
                  placeholder="Votre message..."
                  value={inputMessage}
                  onChange={(e) => setInputMessage(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && handleSendMessage()}
                />
                <SendButton onClick={handleSendMessage} disabled={!inputMessage.trim()}>
                  <FiSend />
                </SendButton>
              </InputArea>
            </>
          ) : (
            <FAQContent>
              <SearchBar>
                <SearchIcon><FiSearch /></SearchIcon>
                <SearchInput
                  type="text"
                  placeholder="Rechercher..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
              </SearchBar>

              <CategoryFilter>
                {categories.map(category => (
                  <CategoryChip
                    key={category}
                    active={selectedCategory === category}
                    onClick={() => setSelectedCategory(category)}
                  >
                    {category}
                  </CategoryChip>
                ))}
              </CategoryFilter>

              <FAQList>
                {filteredFAQ.length > 0 ? (
                  filteredFAQ.map(item => (
                    <FAQItemCard key={item.id}>
                      <FAQQuestion onClick={() => setOpenFAQId(openFAQId === item.id ? null : item.id)}>
                        {item.question}
                        {openFAQId === item.id ? <FiChevronUp /> : <FiChevronDown />}
                      </FAQQuestion>
                      <FAQAnswer isOpen={openFAQId === item.id}>
                        {item.answer}
                      </FAQAnswer>
                    </FAQItemCard>
                  ))
                ) : (
                  <EmptyState>
                    <FiSearch size={36} style={{ marginBottom: theme.spacing.sm }} />
                    <p>Aucune question trouvée</p>
                  </EmptyState>
                )}
              </FAQList>
            </FAQContent>
          )}
        </ChatContent>
      </ChatWindow>
    </>
  );
};
